import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import makeStyles from '@mui/styles/makeStyles';
import { useQuery } from '@tanstack/react-query';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import AccountListActivation from './AccountListActivation';
import { fetchClientList } from './api';
import Promotions from 'react/member/components/referrals/Promotions';
import TrueLinkLink from 'react/shared/components/true_link/main/TrueLinkLink';
import TrueLinkCheckboxes from 'react/shared/components/true_link/main/form/checkboxes/TrueLinkCheckboxes';
import TrueLinkTable from 'react/shared/components/true_link/main/table/TrueLinkTable';
import PALETTE from 'react/shared/theme/palette';
import { getFraudBlockText } from 'react/shared/utils/FraudBlockText';

const useStyles = makeStyles({
  root: {
    position: 'relative',
  },
  table: {
    '& tbody': {
      fontSize: '18px !important',
    },

    '& td': {
      textAlign: 'left',
      padding: '15px 10px',
    },

    '& td:last-child': {
      textAlign: 'right',
      padding: '15px 0px',
    },
    '& .table-filters, & .table-title': {
      display: 'none !important',
    },
  },
  hideClosedCardsCheckbox: {
    position: 'absolute',
    top: '64px',
    left: '260px',
    width: '200px',
  },
});

const compromisedActionNeededTitle =
  "This card has been reported as compromised. Immediate action is required. Please click into the cardholder's dashboard for more information.";
const compromisedTitle =
  "This card has been reported as compromised. The card is currently inactive and immediate action is required. Please click into the cardholder's dashboard for more information.";

export default function ClientListTable({ accountListMessage, showAccountId, showLatestTransfer }) {
  const classes = useStyles();
  const [hideClosedCards, setHideClosedCards] = useState(true);
  const [openActivateCardModal, setOpenActivateCardModal] = useState(false);
  const [activeRow, setActiveRow] = useState(null);
  const {
    data: rawData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['client-list'],
    queryFn: () => fetchClientList(),
  });

  // Filter data if hideClosedCards is true
  const data = useMemo(() => {
    if (!rawData) return [];
    return hideClosedCards ? rawData.filter((card) => card.cardStatus !== 'CLOSED') : rawData;
  }, [rawData, hideClosedCards]);

  const cardStatusPriority = useMemo(
    () => ({
      FRAUD_BLOCK: 0,
      COMPROMISED_ACTION_NEEDED: 1,
      COMPROMISED: 2,
    }),
    [],
  );

  const initialSortBy = useMemo(
    () => [
      { id: 'cardStatus', desc: false },
      { id: 'lastName', desc: false },
    ],
    [],
  );

  const activateCard = (row) => {
    if (row.slug && (!row.bulkCardOrder || (row.bulkCardOrder && row.isReissue))) {
      //New funding flow for all non VCC card or VCC Reissue A card
      window.location.href = `${RailsRoutes.select_account_path(row.slug)}#account_info_tab_link`;
      Cookies.set('activate_card_modal', true, { expires: 1 / 1440 });
    } else {
      setActiveRow(row);
      setOpenActivateCardModal(true);
    }
  };

  const formatDashboard = (row) => (
    <a href={`${RailsRoutes.select_account_path(row.slug)}`}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        Dashboard
        <NavigateNextIcon sx={{ fontSize: 16 }} />
      </Box>
    </a>
  );

  const formatStatus = (status, title) => (
    <Tooltip arrow placement="top" title={title}>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {status}
        <RemoveCircleIcon sx={{ fontSize: '24px', fill: PALETTE.red }} />
      </Box>
    </Tooltip>
  );

  const formatTransferAmount = (row) =>
    row.transferAmount && row.transferDate
      ? `$${row.transferAmount} on ${moment(row.transferDate).format('MMM D')}`
      : '';

  const formatCardStatus = (row) => {
    switch (row.cardStatus) {
      case 'FRAUD_BLOCK':
        return formatStatus('Fraud Block', getFraudBlockText(row.cardFraudBlockType));
      case 'COMPROMISED_ACTION_NEEDED':
        return formatStatus('Compromised Action Needed', compromisedActionNeededTitle);
      case 'COMPROMISED':
        return formatStatus('Compromised', compromisedTitle);
      case 'ISSUED_INACTIVE':
        return (
          <TrueLinkLink id={row.slug} onClick={() => activateCard(row)}>
            Activate
          </TrueLinkLink>
        );
      case 'OPEN':
        return `$${row.cardBalance}`;
      case 'CLOSED':
        return 'Closed';
      default:
        return 'In Progress';
    }
  };

  const formatStatementDate = (row) => {
    if (row.statementDate) {
      const statementUrl = `${RailsRoutes.account_official_statement_path(
        row.slug,
        row.statementDate,
        { format: 'zip' },
      )}`;
      return <a href={statementUrl}>{row.statementDate}</a>;
    }
    return 'None';
  };

  const memoizedColumns = useMemo(
    () => [
      {
        id: 'nickname',
        Header: 'Account ID',
        accessor: 'nickname',
        width: 100,
        hidden: !showAccountId,
      },
      {
        id: 'firstName',
        Header: 'First Name',
        accessor: 'firstName',
        width: 115,
      },
      {
        id: 'lastName',
        Header: 'Last Name',
        accessor: 'lastName',
        width: 115,
      },
      {
        id: 'cardStatus',
        Header: 'Status',
        accessor: 'cardStatus',
        sortType: (rowA, rowB, columnId) => {
          const a = cardStatusPriority[rowA.values[columnId]] ?? 99;
          const b = cardStatusPriority[rowB.values[columnId]] ?? 99;
          return a - b;
        },
        Cell: ({ row }) => formatCardStatus(row.original),
        width: 140,
      },
      {
        id: 'statementDate',
        Header: 'Statement',
        Cell: ({ row }) => formatStatementDate(row.original),
        width: 80,
        disableSort: true,
      },
      {
        id: 'transferAmount',
        Header: 'Latest funds transfer',
        accessor: 'transferAmount',
        Cell: ({ row }) => formatTransferAmount(row.original),
        width: 140,
        hidden: !showLatestTransfer,
        disableSort: true,
      },
      {
        id: 'dashboard',
        Cell: ({ row }) => formatDashboard(row.original),
        width: 50,
        disableSort: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cardStatusPriority, showAccountId, showLatestTransfer],
  );

  return (
    <div className={classes.root}>
      <Promotions />
      {accountListMessage && (
        <div className="info-box" style={{ marginBottom: 15 }}>
          {accountListMessage}
        </div>
      )}
      {data && (
        <>
          <div className={classes.hideClosedCardsCheckbox}>
            <TrueLinkCheckboxes
              ariaLabel="Hide Closed Cards"
              name="hideClosedCards"
              onChange={() => setHideClosedCards(!hideClosedCards)}
              options={[{ label: 'Hide Closed Cards', value: 'true' }]}
              value={hideClosedCards ? ['true'] : []}
            />
          </div>
          <TrueLinkTable
            className={classes.table}
            columns={memoizedColumns}
            data={data}
            initialSortBy={initialSortBy}
            isFilterable
            loading={isLoading}
            pageSize={25}
            paginated
            resizable={false}
            showPagination
            sortable
          />
        </>
      )}
      {isError && (
        <div className="error-box" style={{ marginBottom: 15 }}>
          Failed to load client list. Please try again later.
        </div>
      )}

      {openActivateCardModal && (
        <AccountListActivation data={activeRow} onClose={() => setOpenActivateCardModal(false)} />
      )}
    </div>
  );
}

ClientListTable.propTypes = {
  accountListMessage: PropTypes.string,
  showAccountId: PropTypes.bool.isRequired,
  showLatestTransfer: PropTypes.bool.isRequired,
};
